import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import type { Editor } from '@tiptap/core';

// ── Display ──────────────────────────────────────────────────────────────────

interface RichTextContentProps { html: string; className?: string; }

export function RichTextContent({ html, className }: RichTextContentProps) {
  if (!html) return null;
  const content = html.trimStart().startsWith('<') ? html : `<p>${html}</p>`;
  return (
    <div
      className={`rich-content${className ? ` ${className}` : ''}`}
      // TipTap produces sanitized HTML; plain-text fallback is safe
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

function Btn({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className={`rich-editor-btn${active ? ' rich-editor-btn--active' : ''}`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="rich-editor-sep" />;
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  // Capture non-null ref so TypeScript doesn't complain inside the closure
  const e = editor;

  function handleLink() {
    const prev = (e.getAttributes('link').href as string) ?? '';
    const url = window.prompt('Link URL (leave empty to remove):', prev);
    if (url === null) return;
    if (url.trim() === '') {
      e.chain().focus().unsetLink().run();
    } else {
      e.chain().focus().setLink({ href: url.trim() }).run();
    }
  }

  return (
    <div className="rich-editor-toolbar">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)">
        <strong>B</strong>
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)">
        <em>I</em>
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)">
        <u>U</u>
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <s>S</s>
      </Btn>

      <Sep />

      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
        H1
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
        H2
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
        H3
      </Btn>

      <Sep />

      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
        • List
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
        1. List
      </Btn>

      <Sep />

      <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code">
        {'`code`'}
      </Btn>
      <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block">
        {'```'}
      </Btn>

      <Sep />

      <Btn onClick={handleLink} active={editor.isActive('link')} title="Link">
        Link
      </Btn>
    </div>
  );
}

// ── Editor ────────────────────────────────────────────────────────────────────

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
}

export default function RichTextEditor({ value, onChange, minHeight = 100 }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (current !== incoming) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div className="rich-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} style={{ minHeight }} />
    </div>
  );
}
